import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Building2, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/stores/authStore';
import { parseOrders, parsePositions } from '@/stores/accountStore';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { describeMarket } from '@/lib/kalshiTicker';
import {
  sportFromTicker,
  sportMatchesFilter,
  cycleSportFilter,
  type SportFilter,
} from '@/lib/kalshiTicker/sportFilter';
import { SPORT_TABS, type Sport } from '@/views/ValueDashboard/types';
import {
  SummaryTile,
  SummaryStrip,
  ViewModeToggle,
  CardGrid,
  CompactListGrid,
} from '@/components/cards/cardKit';
import {
  fmtLastFillAt,
  fmtUsd,
  fmtUsdSigned,
  titleParts,
} from '@/components/cards/cardFormat';
import { PositionCard, CompactPositionItem } from '@/components/cards/PositionCard';
import {
  buildPolyPositionVM,
  fullPositionAnchor,
  type PositionCardVM,
} from '@/components/cards/positionCardModel';
import { americanOdds } from '@/views/ValueDashboard/tradeModalBook';
import {
  computeOrgTraderUnrealized,
  type OrgTraderRow,
} from '@/components/organisms/OrgTradersTable';
import { describePolyMarketCached, polyPositionLiveData } from '@/lib/polymarket/describeOrder';
import { estimateAggregateFeeCents, takerRateForTicker } from '@/lib/pricing/fees';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import type { PolyFeeSchedule } from '@/lib/polymarket/relay';
import {
  UNATTRIBUTED_OWNER,
  ownerMatchesFilter,
  resolveOwnerAttribution,
  type OwnerAttribution,
  type OwnerFilterKey,
} from '@/lib/ownerAttribution';
import {
  kalshiReportedBalanceTitle,
  readKalshiReportedBalanceCents,
} from '@/lib/kalshiBalance';
import {
  orgRowKey,
  reconciledOrgLifetimeSource,
  type OrgPositionRow,
} from './orgRowKey';

// Same dense→table→card→mini choices as the Positions page. positionViewMode
// is a shared persisted preference, so the org view tracks whatever the trader
// last picked on /positions.
const POSITION_VIEW_MODES = ['compact', 'table', 'card', 'mini'] as const;

interface OrgDetailProps {
  /** Admin path: the org being inspected (sent as ?orgId). Omit for the
   *  manager "own org" path — the relay scopes to the caller's org. */
  orgId?: string;
  orgName?: string;
  /** Omit on the manager nav surface (no parent to go back to). */
  onBack?: () => void;
  onTraderClick: (row: OrgTraderRow) => void;
}

// One resting order on the org account, tagged with its owning trader
// (from /org/orders-by-trader — null owner = unattributed, the failsafe truth).
interface OrgOrderRow {
  orderId: string;
  owner: OwnerAttribution;
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  priceCents: number | null;
  sport: Sport | null;
}

interface OrgFillRow {
  fillId: string;
  orderId: string | null;
  owner: OwnerAttribution;
  venue: 'kalshi' | 'polymarket';
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  qty: number;
  priceCents: number | null;
  feeCents: number | null;
  executedAt: string;
  sport: Sport | null;
}

// One sibling-instance position from /org/remote-positions (the relay's fold
// of remote-attributed fills). Rendered only when it explains part of a live
// venue position — settled markets drop out with the venue snapshot.
interface RemotePositionWire {
  surface: string;
  remoteUserId: string;
  remoteUsername: string;
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  avgCents: number;
  costBasisCents: number;
  feesPaidCents: number | null;
  lastFillAt: string | null;
}

// Per-sibling sync health from /org/attribution-sync-status.
interface SiblingSyncStatus {
  code: string;
  url: string;
  lastOkAt: string | null;
  lastError: string | null;
  rowsMirrored: number;
}

function SportFilterChip({
  mode,
  label,
  onClick,
}: {
  mode: 'off' | 'include' | 'exclude';
  label: string;
  onClick: () => void;
}) {
  const cls =
    mode === 'include'
      ? 'border-primary/50 bg-primary/15 text-primary'
      : mode === 'exclude'
        ? 'border-red-500/50 bg-red-500/10 text-red-400 line-through'
        : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${cls}`}
    >
      {label}
    </button>
  );
}

function SideBadge({ side, betTeam }: { side: 'yes' | 'no'; betTeam: string | null }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
        side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}
    >
      {betTeam ? `(${betTeam}) ` : ''}
      {side.toUpperCase()}
    </span>
  );
}

function OwnerTag({ owner }: { owner: OwnerAttribution }) {
  if (owner.kind === 'bot') {
    return (
      <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-400" title={owner.label ?? 'Bot-owned order'}>
        {owner.label ?? 'bot'}
      </span>
    );
  }
  if (owner.kind === 'remote') {
    return (
      <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-400" title="Placed by a trader on a sibling instance (shared org keys, separate DB) — attributed via the cross-instance ledger sync.">
        {owner.label ?? 'remote'}
      </span>
    );
  }
  if (owner.kind === 'unattributed') {
    return (
      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400" title="No ledger row on this instance or any synced sibling — placed outside the app or before tracking. Still really on Kalshi.">
        unattributed
      </span>
    );
  }
  return <span className="text-foreground/90 text-xs">{owner.label}</span>;
}

function VenueTag({ venue }: { venue: 'kalshi' | 'polymarket' }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
      venue === 'polymarket'
        ? 'bg-violet-500/15 text-violet-400'
        : 'bg-sky-500/15 text-sky-400'
    }`}>
      {venue === 'polymarket' ? 'POLY' : 'KALSHI'}
    </span>
  );
}

// Build a PositionCard VM from an attributed org row + the live mark, so the
// card / compact / mini views render identically to the Positions page. Read-
// only here (editable: false) — the admin/manager doesn't close traders'
// positions from this aggregate view.
function orgPositionVM(
  p: OrgPositionRow,
  nowCents: number | null,
  polyFeeSchedule?: PolyFeeSchedule | null,
): PositionCardVM {
  const lifetime = reconciledOrgLifetimeSource(p);
  if (p.venue === 'polymarket') {
    return buildPolyPositionVM({
      ticker: p.ticker,
      side: p.side,
      netCount: p.qty,
      avgEntryPriceCents: p.avgCents,
      lastFillAt: p.lastFillAt,
      costBasisCents: p.costBasisCents,
      realizedPnlCents: 0,
      feesPaidCents: p.feesPaidCents,
      openFeesCents: p.openFeesCents,
      ...lifetime,
    }, null, nowCents, polyFeeSchedule);
  }
  const { title, subtitle, team } = titleParts(p.ticker, p.side);
  // Open-share fees only (never the bucket total — sold-share fees belong to
  // realized). Unknown allocation → P&L withheld below, not priced as $0-fee.
  const openFeesKnown = p.openFeesCents != null;
  const entryCostCents = p.costBasisCents + (p.openFeesCents ?? 0);
  const takerRate = takerRateForTicker(p.ticker);
  const closingFeeCents = nowCents != null && takerRate != null
    ? estimateAggregateFeeCents({ priceCents: nowCents, count: p.qty, feeRate: takerRate })
    : 0;
  const cashOutCents = nowCents != null ? nowCents * p.qty - closingFeeCents : null;
  const pnlCents = cashOutCents != null && openFeesKnown ? cashOutCents - entryCostCents : null;
  return {
    key: `${p.userId}-${p.ticker}-${p.side}`,
    venue: p.venue,
    ticker: p.ticker,
    title,
    subtitle,
    eventStartLabel: null,
    lastFillAt: p.lastFillAt,
    side: p.side,
    team,
    qty: p.qty,
    pendingSellQty: 0,
    // Full-position anchor (#91/#175): render only when the manager rollup's
    // authoritative lifetime totals reconcile exactly to the held remainder.
    // Remote/residual rows carry no such evidence and stay dashed.
    ...fullPositionAnchor(lifetime),
    avgCents: p.avgCents,
    avgOdds: americanOdds(p.qty > 0 ? p.costBasisCents / p.qty : p.avgCents),
    feesCents: p.feesPaidCents ?? null,
    costDollars: entryCostCents / 100,
    nowCents,
    nowOdds: nowCents != null ? americanOdds(Math.round(nowCents)) : null,
    pnlDollars: pnlCents != null ? pnlCents / 100 : null,
    cashOutDollars: cashOutCents != null ? cashOutCents / 100 : null,
    ifWinDollars: (p.qty * 100 - entryCostCents) / 100,
    grossPayoutDollars: p.qty,
    impliedOdds: p.qty > 0 ? americanOdds(p.costBasisCents / p.qty) : null,
    editable: false,
  };
}

export function OrgDetail({ orgId, orgName, onBack, onTraderClick }: OrgDetailProps) {
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  // Attributed rows (per-trader ledgers) and residual unattributed rows (venue
  // snapshot minus attributed) land from different fetches; keeping them in
  // separate state and merging at render removes any landing-order dependency.
  const [positions, setPositions] = useState<OrgPositionRow[]>([]);
  // Sibling-instance rows (cross-instance ledger sync) sit between the two:
  // attributed, but not to a local trader. They subtract from the residual
  // exactly like local attribution does.
  const [remotes, setRemotes] = useState<OrgPositionRow[]>([]);
  const [residuals, setResiduals] = useState<OrgPositionRow[]>([]);
  const [syncSiblings, setSyncSiblings] = useState<SiblingSyncStatus[]>([]);
  const [syncFetchedAt, setSyncFetchedAt] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrgOrderRow[]>([]);
  const [fills, setFills] = useState<OrgFillRow[]>([]);
  const [members, setMembers] = useState<OrgTraderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [fillsLoading, setFillsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Monotonic fetch generation: an org switch or refresh mid-flight must not
  // let a stale response write state for the wrong org.
  const fetchGen = useRef(0);
  const [sportFilter, setSportFilter] = useState<SportFilter>(() => new Map());
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilterKey>('all');

  // Live mark for the Now / unrealized columns. version drives re-render on
  // every hydra tick; getMarketPrice returns the YES mid (cents).
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const toggleShowPnl = useDisplaySettings((s) => s.toggleShowPnl);
  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);

  const orgQueryParam = orgId ? `?orgId=${orgId}` : '';

  const fetchAll = useCallback(async () => {
    const gen = ++fetchGen.current;
    const fresh = () => fetchGen.current === gen;
    setLoading(true);
    setOrdersLoading(true);
    setFillsLoading(true);
    setError(null);
    // Residuals belong to a (generation, account-fetch) pair; without this
    // reset an org switch could merge org A's residual rows into org B's view
    // until B's account fetch lands. Remote rows ride the same fetch pair.
    setResiduals([]);
    setRemotes([]);

    // Partial-failure signal, priority-ranked (core 0 > orders 1 > fills 2 >
    // balance 3) so a fast trivial failure can't shadow a consequential one —
    // and an empty table never reads as "nothing on Kalshi" when really a
    // fetch failed. Rank state is per-generation (closure-local).
    let errorRank = Number.POSITIVE_INFINITY;
    const softFail = (rank: number, msg: string) => {
      if (rank < errorRank) {
        errorRank = rank;
        setError(msg);
      }
    };

    // Four independent fetches, four independent landings. The shell gates
    // ONLY on positions-by-trader; orders, fills, and the account snapshot
    // each hydrate their own panel when ready. Never gate the page on its
    // slowest fetch (fills-by-owner walks the org fill history and is the
    // slow one under load).
    const posDataP = apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`)
      .then(async (res) => ({
        ok: res.ok,
        data: (await res.json().catch(() => null)) as { rows?: OrgTraderRow[]; error?: string } | null,
      }))
      .catch(() => ({ ok: false, data: null }));

    // Attributed rows resolve once, shared by the shell and the residual calc.
    const attributedRowsP: Promise<OrgPositionRow[] | null> = posDataP.then(({ ok, data }) => {
      if (!ok || !data?.rows) return null;
      // Positions: flatten each trader's openPositions, tagging the owner.
      return data.rows.flatMap((r) =>
        (r.openPositions ?? []).map((p) => ({
          userId: r.userId,
          owner: resolveOwnerAttribution({
            ownerKind: 'trader',
            ownerUserId: r.userId,
            ownerUsername: r.username,
          }),
          venue: p.venue ?? 'kalshi',
          ticker: p.ticker,
          side: p.side,
          qty: p.netCount,
          avgCents: p.avgEntryPriceCents,
          lastFillAt: p.lastFillAt ?? null,
          // Exact ledger cost basis when present, else avg × contracts.
          costBasisCents: p.costBasisCents ?? Math.round(p.avgEntryPriceCents * p.netCount),
          feesPaidCents: p.feesPaidCents,
          openFeesCents: p.openFeesCents,
          totalBoughtCount: p.totalBoughtCount,
          totalSoldCount: p.totalSoldCount,
          totalBuyCostCents: p.totalBuyCostCents,
          sport: p.venue === 'polymarket'
            ? (describePolyMarketCached(p.ticker)?.sport ?? null)
            : sportFromTicker(p.ticker),
        })),
      );
    });

    // Core panel: traders + attributed positions.
    void (async () => {
      const [{ data }, posRows] = await Promise.all([posDataP, attributedRowsP]);
      if (!fresh()) return;
      if (posRows == null) {
        softFail(
          0,
          typeof data?.error === 'string'
            ? data.error
            : 'Could not load trader attribution — the org may not have a Kalshi credential configured; other panels may be incomplete.',
        );
        setPositions([]);
        setResiduals([]);
        setMembers([]);
        return;
      }
      setMembers(data?.rows ?? []);
      // Residual (unattributed) rows land separately from the account fetch.
      setPositions(posRows);
    })()
      .catch(() => {
        if (fresh()) softFail(0, 'Could not load org data.');
      })
      .finally(() => {
        if (fresh()) setLoading(false);
      });

    // Sibling-instance positions resolve alongside the account snapshot; a
    // failure (or a non-master relay with no mirror) degrades cleanly to the
    // old residual-only behavior.
    const remotePosP: Promise<RemotePositionWire[] | null> = apiFetch(
      `/api/manager/org/remote-positions${orgQueryParam}`,
    )
      .then(async (res) =>
        res.ok
          ? (((await res.json().catch(() => null)) as { positions?: RemotePositionWire[] } | null)
              ?.positions ?? [])
          : null,
      )
      .catch(() => null);

    // Account panel: balance now, remote + residual position rows once the
    // snapshot, the attributed rows, and the remote fold are all in (they
    // fetch concurrently).
    void (async () => {
      const acctRes = await apiFetch(`/api/manager/org/account${orgQueryParam}`).catch(() => null);
      const acctData = acctRes
        ? ((await acctRes.json().catch(() => null)) as
            | { balance?: Record<string, unknown>; positions?: unknown[]; error?: string }
            | null)
        : null;
      const posRows = await attributedRowsP;
      const remoteWire = await remotePosP;
      if (!fresh()) return;

      const bal = acctData?.balance ?? null;
      setBalanceCents(bal ? readKalshiReportedBalanceCents(bal) : null);
      if (!acctRes?.ok) softFail(3, 'Balance did not load.');
      if (posRows == null) return; // core failed — nothing to attribute against

      const venuePositions = parsePositions({ positions: acctData?.positions ?? [] });
      const venueKeys = new Set(venuePositions.map((p) => `${p.ticker}::${p.side}`));

      // Remote rows render only where they explain part of a LIVE venue
      // position — the remote fold spans full fill history, so settled
      // markets (which the venue snapshot no longer lists) drop out here.
      const remoteRows: OrgPositionRow[] = (remoteWire ?? [])
        .filter((r) => venueKeys.has(`${r.ticker}::${r.side}`))
        .map((r) => ({
          userId: `remote:${r.surface}:${r.remoteUserId}`,
          owner: resolveOwnerAttribution({
            ownerKind: 'remote',
            remoteSurface: r.surface,
            remoteUserId: r.remoteUserId,
            remoteUsername: r.remoteUsername,
          }),
          venue: 'kalshi' as const,
          ticker: r.ticker,
          side: r.side,
          qty: r.qty,
          avgCents: r.avgCents,
          lastFillAt: r.lastFillAt,
          costBasisCents: r.costBasisCents,
          feesPaidCents: r.feesPaidCents ?? undefined,
          sport: sportFromTicker(r.ticker),
        }));
      setRemotes(remoteRows);

      // Residual = venue − locally attributed − remote-attributed. What's
      // left is genuinely unattributed (external automation on the shared
      // keys, or a sibling not yet synced).
      const attributedQty = new Map<string, number>();
      for (const p of [...posRows, ...remoteRows]) {
        const key = `${p.ticker}::${p.side}`;
        attributedQty.set(key, (attributedQty.get(key) ?? 0) + p.qty);
      }
      const residualRows: OrgPositionRow[] = venuePositions.flatMap((p) => {
        const key = `${p.ticker}::${p.side}`;
        const residual = p.position - (attributedQty.get(key) ?? 0);
        if (residual <= 0.0001) return [];
        return [{
          userId: 'unattributed',
          owner: UNATTRIBUTED_OWNER,
          venue: 'kalshi' as const,
          ticker: p.ticker,
          side: p.side,
          qty: residual,
          avgCents: p.averagePrice,
          lastFillAt: p.lastFillAt ?? null,
          costBasisCents: p.costBasisCents ?? Math.round(p.averagePrice * residual),
          sport: sportFromTicker(p.ticker),
        }];
      });
      setResiduals(residualRows);
    })().catch(() => {
      if (fresh()) softFail(3, 'Account snapshot did not load — balance and unattributed positions may be missing.');
    });

    // Sync-health strip — independent, cosmetic-only landing. Empty siblings
    // (every non-master instance) renders nothing.
    void apiFetch(`/api/manager/org/attribution-sync-status${orgQueryParam}`)
      .then(async (res) => {
        if (!res.ok || !fresh()) return;
        const data = (await res.json().catch(() => null)) as
          | { siblings?: SiblingSyncStatus[]; fetchedAt?: string }
          | null;
        if (!fresh()) return;
        setSyncSiblings(data?.siblings ?? []);
        setSyncFetchedAt(data?.fetchedAt ?? null);
      })
      .catch(() => {});

    // Orders panel — independent landing.
    void (async () => {
      const ordRes = await apiFetch(`/api/manager/org/orders-by-trader${orgQueryParam}`).catch(() => null);
      const ordData = ordRes
        ? ((await ordRes.json().catch(() => null)) as
            | { orders?: Array<Record<string, unknown>>; error?: string }
            | null)
        : null;
      if (!fresh()) return;

      // Orders: parse the raw Kalshi orders with the canonical helper, then
      // re-attach the owner tag the relay added (parseOrders drops extras).
      const rawOrders = ordData?.orders ?? [];
      const ownerByOrderId = new Map<string, OwnerAttribution>();
      for (const o of rawOrders) {
        const id = o.order_id;
        if (typeof id === 'string') {
          ownerByOrderId.set(id, resolveOwnerAttribution(o));
        }
      }
      const parsed = parseOrders({ orders: rawOrders });
      const ordRows: OrgOrderRow[] = parsed.map((o) => {
        const owner = ownerByOrderId.get(o.orderId) ?? UNATTRIBUTED_OWNER;
        const sidePrice =
          o.side === 'no'
            ? (o.noPrice ?? (o.yesPrice != null ? 100 - o.yesPrice : null))
            : (o.yesPrice ?? (o.noPrice != null ? 100 - o.noPrice : null));
        return {
          orderId: o.orderId,
          owner,
          ticker: o.ticker,
          side: o.side,
          qty: o.remainingCount,
          priceCents: sidePrice,
          sport: sportFromTicker(o.ticker),
        };
      });
      setOrders(ordRows);
      if (!ordRes?.ok) softFail(1, 'Orders did not load — the order list may be incomplete.');
    })()
      .catch(() => {
        if (fresh()) softFail(1, 'Orders did not load — the order list may be incomplete.');
      })
      .finally(() => {
        if (fresh()) setOrdersLoading(false);
      });

    // Trades panel — independent landing (the heavy fetch under load).
    void (async () => {
      const fillsRes = await apiFetch(`/api/manager/org/fills-by-owner${orgQueryParam}`).catch(() => null);
      const fillsData = fillsRes
        ? ((await fillsRes.json().catch(() => null)) as
            | { fills?: Array<Record<string, unknown>>; error?: string }
            | null)
        : null;
      if (!fresh()) return;

      const fillRows: OrgFillRow[] = (fillsData?.fills ?? []).map((f) => {
        const owner = resolveOwnerAttribution(f);
        const side = f.side === 'no' ? 'no' : 'yes';
        const ticker = String(f.ticker ?? '');
        return {
          fillId: String(f.fill_id ?? f.trade_id ?? ''),
          orderId: typeof f.order_id === 'string' ? f.order_id : null,
          owner,
          venue: f.venue === 'polymarket' ? 'polymarket' : 'kalshi',
          ticker,
          side,
          action: f.action === 'sell' ? 'sell' : 'buy',
          qty: Number(f.count ?? 0),
          priceCents: typeof f.price === 'number' ? f.price : null,
          feeCents: typeof f.fee_cents === 'number' ? f.fee_cents : null,
          executedAt: String(f.created_time ?? ''),
          sport: f.venue === 'polymarket'
            ? (describePolyMarketCached(ticker)?.sport ?? null)
            : sportFromTicker(ticker),
        };
      });
      setFills(fillRows);
      if (!fillsRes?.ok) softFail(2, 'Trades did not load — the fill tape may be incomplete.');
    })()
      .catch(() => {
        if (fresh()) softFail(2, 'Trades did not load — the fill tape may be incomplete.');
      })
      .finally(() => {
        if (fresh()) setFillsLoading(false);
      });
  }, [orgQueryParam]);

  useEffect(() => {
    fetchAll();
    // Invalidate in-flight handlers on unmount/org-switch so no stale landing
    // writes state afterward.
    return () => {
      fetchGen.current += 1;
    };
  }, [fetchAll]);

  useEffect(() => {
    // An owner key is only meaningful inside the org that supplied it.
    setOwnerFilter('all');
  }, [orgQueryParam]);

  // Side-native current price for a ticker (cents), from the live hydra mark.
  const nowCentsFor = useCallback(
    (venue: 'kalshi' | 'polymarket', ticker: string, side: 'yes' | 'no'): number | null => {
      if (venue === 'polymarket') return polyPositionLiveData(ticker, side).nowCents;
      const yesMid = getMarketPrice(ticker);
      if (yesMid == null) return null;
      return side === 'yes' ? yesMid : 100 - yesMid;
    },
    // marketVersion forces re-eval as prices tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getMarketPrice, marketVersion],
  );

  // Attributed + remote + residual rows, merged at render (landing-order
  // independent).
  const allPositions = useMemo(
    () => [...positions, ...remotes, ...residuals],
    [positions, remotes, residuals],
  );
  const ownerOptions = useMemo(() => {
    const options = new Map<OwnerFilterKey, string>([['all', 'All owners']]);
    for (const member of members) {
      options.set(`trader:${member.userId}`, member.username);
    }
    for (const owner of [
      ...allPositions.map((row) => row.owner),
      ...orders.map((row) => row.owner),
      ...fills.map((row) => row.owner),
    ]) {
      if (owner.kind === 'unattributed') {
        options.set('unattributed', 'Unattributed');
      } else {
        options.set(owner.filterKey, owner.label ?? owner.filterKey);
      }
    }
    return [...options].map(([key, label]) => ({ key, label }));
  }, [allPositions, fills, members, orders]);

  useEffect(() => {
    if (!ownerOptions.some((option) => option.key === ownerFilter)) {
      setOwnerFilter('all');
    }
  }, [ownerFilter, ownerOptions]);

  const polyFeeConditionIds = useMemo(
    () => allPositions.filter((position) => position.venue === 'polymarket').map((position) => position.ticker),
    [allPositions],
  );
  const { schedules: polyFeeSchedules } = usePolyFeeSchedules(polyFeeConditionIds);
  const visiblePositions = useMemo(
    () => allPositions.filter(
      (p) => sportMatchesFilter(p.sport, sportFilter) && ownerMatchesFilter(p.owner, ownerFilter),
    ),
    [allPositions, ownerFilter, sportFilter],
  );
  const visibleOrders = useMemo(
    () => orders.filter(
      (o) => sportMatchesFilter(o.sport, sportFilter) && ownerMatchesFilter(o.owner, ownerFilter),
    ),
    [orders, ownerFilter, sportFilter],
  );
  const visibleFills = useMemo(
    () => fills.filter(
      (f) => sportMatchesFilter(f.sport, sportFilter) && ownerMatchesFilter(f.owner, ownerFilter),
    ),
    [fills, ownerFilter, sportFilter],
  );

  const selectedMember = useMemo(() => {
    if (!ownerFilter.startsWith('trader:')) return null;
    const userId = ownerFilter.slice('trader:'.length);
    return members.find((member) => member.userId === userId) ?? null;
  }, [members, ownerFilter]);

  // This is deliberately the SAME calculation used by the per-member rail and
  // OrgTradersTable. A filtered headline cannot drift into a second P&L recipe.
  const selectedMemberPnl = useMemo(() => {
    if (!selectedMember) return null;
    const unrealized = computeOrgTraderUnrealized(
      selectedMember,
      getMarketPrice,
      undefined,
      (ticker) => polyFeeSchedules.get(ticker),
    );
    const realizedNet = selectedMember.realizedPnlNetCents ?? null;
    return {
      valueCents:
        realizedNet != null && unrealized.valueCents != null
          ? realizedNet + unrealized.valueCents
          : null,
      realizedNetKnown: realizedNet != null,
      unrealized,
    };
  }, [getMarketPrice, marketVersion, polyFeeSchedules, selectedMember]);

  // Org aggregates over the visible rows.
  const agg = useMemo(() => {
    let notionalCents = 0;
    let unrealizedCents = 0;
    let pricedPositions = 0;
    for (const p of visiblePositions) {
      notionalCents += p.costBasisCents;
      const now = nowCentsFor(p.venue, p.ticker, p.side);
      if (now != null) {
        const vm = orgPositionVM(p, now, polyFeeSchedules.get(p.ticker));
        if (vm.pnlDollars != null) {
          pricedPositions++;
          unrealizedCents += vm.pnlDollars * 100;
        }
      }
    }
    const totalPositions = visiblePositions.length;
    return {
      notionalCents,
      unrealizedCents,
      pricedPositions,
      totalPositions,
      unrealizedComplete: pricedPositions === totalPositions,
    };
  }, [visiblePositions, nowCentsFor, polyFeeSchedules]);

  const masked = !showPnl;

  return (
    <div className="h-full overflow-auto p-6">
      <div className="flex gap-6">
        {/* Main column — full width */}
        <div className="min-w-0 flex-1 space-y-5">
          {/* Sticky toolbar — same flush-pin pattern as the Positions page. */}
          <div className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky -top-6 z-30 -mx-6 -mt-6 border-b px-6 pb-3 pt-6 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-2 py-1 text-xs"
                >
                  <ArrowLeft size={14} /> Back
                </button>
              )}
              <Building2 size={20} className="text-muted-foreground" />
              <h1 className="text-xl font-semibold">{orgName ?? 'Organization'}</h1>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-muted-foreground flex items-center gap-1 text-[11px]">
                Owner
                <select
                  aria-label="Filter org money surfaces by owner"
                  value={ownerFilter}
                  onChange={(event) => setOwnerFilter(event.target.value as OwnerFilterKey)}
                  className="bg-card border-border text-foreground rounded-md border px-2 py-1 text-xs"
                >
                  {ownerOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filter by sport">
                <SportFilterChip
                  mode={sportFilter.size === 0 ? 'include' : 'off'}
                  label="All sports"
                  onClick={() => setSportFilter(new Map())}
                />
                {SPORT_TABS.map((s) => (
                  <SportFilterChip
                    key={s.id}
                    mode={sportFilter.get(s.id) ?? 'off'}
                    label={s.label}
                    onClick={() => setSportFilter((prev) => cycleSportFilter(prev, s.id))}
                  />
                ))}
              </div>
              <button
                onClick={fetchAll}
                // Re-arm only when every panel has landed — clicking mid-flight
                // would stack fresh fills-by-owner walks on a busy relay.
                disabled={loading || ordersLoading || fillsLoading}
                className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
              >
                <RefreshCw
                  size={14}
                  className={loading || ordersLoading || fillsLoading ? 'animate-spin' : ''}
                />
              </button>
            </div>
          </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
          )}

          {/* Cross-instance attribution sync health (master instances only —
              empty everywhere else). A stale sibling means its traders' new
              orders are temporarily showing as "unattributed", not missing. */}
          {syncSiblings.length > 0 && (
            <div className="text-muted-foreground flex flex-wrap items-center gap-2 px-1 text-[11px]">
              <span>Instance sync</span>
              {syncSiblings.map((s) => {
                const baseMs = syncFetchedAt ? new Date(syncFetchedAt).getTime() : Date.now();
                const ageS = s.lastOkAt
                  ? Math.max(0, Math.round((baseMs - new Date(s.lastOkAt).getTime()) / 1000))
                  : null;
                const stale = ageS == null || ageS > 120 || s.lastError != null;
                return (
                  <span
                    key={s.code}
                    className={`rounded border px-1.5 py-0.5 ${
                      stale
                        ? 'border-amber-500/40 bg-amber-500/10 text-amber-400'
                        : 'border-border'
                    }`}
                    title={
                      s.lastError ??
                      `${s.url} — ${s.rowsMirrored} rows mirrored since relay start`
                    }
                  >
                    {s.code} ·{' '}
                    {ageS == null
                      ? 'never synced'
                      : ageS < 90
                        ? `${ageS}s ago`
                        : `${Math.round(ageS / 60)}m ago`}
                  </span>
                );
              })}
            </div>
          )}

          {/* Position economics come from the combined fills ledger. Balance
              and resting orders remain venue-native Kalshi reads. */}
          <SummaryStrip>
            <SummaryTile
              label="Org Balance (Kalshi · venue-reported)"
              value={balanceCents != null ? fmtUsd(balanceCents / 100) : '—'}
              title={kalshiReportedBalanceTitle('org')}
            />
            <SummaryTile label="Open Positions (K+P)" value={String(visiblePositions.length)} />
            <SummaryTile label="Notional (K+P)" value={fmtUsd(agg.notionalCents / 100)} />
            <SummaryTile label="Open Orders (Kalshi)" value={String(visibleOrders.length)} />
            <SummaryTile label="Trades (K+P)" value={String(visibleFills.length)} />
            <SummaryTile
              label={`Unrealized P&L (K+P)${agg.unrealizedComplete ? '' : ` · ${agg.pricedPositions}/${agg.totalPositions} priced`}`}
              value={masked ? '•••' : agg.unrealizedComplete ? fmtUsdSigned(agg.unrealizedCents / 100) : '—'}
              tone={masked || !agg.unrealizedComplete ? 'default' : agg.unrealizedCents >= 0 ? 'positive' : 'danger'}
            />
            {selectedMember && selectedMemberPnl && (
              <SummaryTile
                label={`Trader P&L (K+P, net · all sports)${selectedMemberPnl.valueCents == null ? ' · fee/mark incomplete' : ''}`}
                value={masked ? '•••' : selectedMemberPnl.valueCents == null ? '—' : fmtUsdSigned(selectedMemberPnl.valueCents / 100)}
                tone={
                  masked || selectedMemberPnl.valueCents == null
                    ? 'default'
                    : selectedMemberPnl.valueCents >= 0
                      ? 'positive'
                      : 'danger'
                }
              />
            )}
            {selectedMember && (
              <SummaryTile
                label={`Realized fees (K+P · all sports)${selectedMember.realizedFeesCents == null ? ' · incomplete' : ''}`}
                value={
                  masked
                    ? '•••'
                    : selectedMember.realizedFeesCents == null
                      ? '—'
                      : fmtUsd(selectedMember.realizedFeesCents / 100)
                }
              />
            )}
          </SummaryStrip>

          {/* Org positions — attributed. Same view-mode choices as /positions
              (shared positionViewMode); the table keeps the full attributed
              column layout, cards/compact carry the owner via `attribution`. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-foreground text-sm font-medium">
                Open Positions <span className="text-muted-foreground">· {visiblePositions.length}</span>
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleShowPnl}
                  className="text-muted-foreground hover:text-foreground rounded p-1"
                  title={showPnl ? 'Hide P&L' : 'Show P&L'}
                >
                  {showPnl ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
                {visiblePositions.length > 0 && (
                  <ViewModeToggle
                    value={positionViewMode}
                    modes={POSITION_VIEW_MODES}
                    onChange={setPositionViewMode}
                  />
                )}
              </div>
            </div>
            {loading ? (
              <div className="bg-card border-border flex justify-center rounded-lg border py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : visiblePositions.length === 0 ? (
              <div className="bg-card border-border rounded-lg border">
                <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open positions</p>
              </div>
            ) : positionViewMode === 'card' || positionViewMode === 'mini' ? (
              <CardGrid variant={positionViewMode === 'mini' ? 'mini' : 'default'}>
                {visiblePositions.map((p) => {
                  const vm = orgPositionVM(
                    p,
                    nowCentsFor(p.venue, p.ticker, p.side),
                    polyFeeSchedules.get(p.ticker),
                  );
                  return (
                    <PositionCard
                      key={orgRowKey(p)}
                      vm={vm}
                      hidePnl={!showPnl}
                      mini={positionViewMode === 'mini'}
                      onEdit={() => {}}
                      attribution={<OwnerTag owner={p.owner} />}
                    />
                  );
                })}
              </CardGrid>
            ) : positionViewMode === 'compact' ? (
              <CompactListGrid>
                {visiblePositions.map((p) => {
                  const vm = orgPositionVM(
                    p,
                    nowCentsFor(p.venue, p.ticker, p.side),
                    polyFeeSchedules.get(p.ticker),
                  );
                  return (
                    <CompactPositionItem
                      key={orgRowKey(p)}
                      vm={vm}
                      hidePnl={!showPnl}
                      onEdit={() => {}}
                      attribution={<OwnerTag owner={p.owner} />}
                    />
                  );
                })}
              </CompactListGrid>
            ) : (
              <div className="bg-card border-border rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Venue</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Avg</th>
                    <th className="px-3 py-2 text-right">Last fill</th>
                    <th className="px-3 py-2 text-right">Now</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2 text-right">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePositions.map((p) => {
                    const now = nowCentsFor(p.venue, p.ticker, p.side);
                    const vm = orgPositionVM(p, now, polyFeeSchedules.get(p.ticker));
                    const marketLabel = vm.title;
                    const betTeam = vm.team;
                    const pnlCents = vm.pnlDollars != null ? vm.pnlDollars * 100 : null;
                    return (
                      <tr key={orgRowKey(p)} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><VenueTag venue={p.venue} /></td>
                        <td className="px-3 py-2"><OwnerTag owner={p.owner} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={p.side} betTeam={betTeam} /></td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(p.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">{p.avgCents}¢</td>
                        <td
                          className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                          title={p.lastFillAt ?? undefined}
                        >
                          {fmtLastFillAt(p.lastFillAt)}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {now != null ? `${now}¢` : '—'}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {fmtUsd(p.costBasisCents / 100)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono text-xs ${
                            masked || pnlCents == null
                              ? 'text-muted-foreground'
                              : pnlCents >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                        >
                          {masked ? '•••' : pnlCents != null ? fmtUsdSigned(pnlCents / 100) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {/* Org orders — attributed */}
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">
                Open Orders <span className="text-muted-foreground">· {visibleOrders.length}</span>
              </h2>
            </div>
            {visibleOrders.length === 0 && ordersLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : visibleOrders.length === 0 ? (
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open orders</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((o, i) => {
                    const { marketLabel, betTeam } = describeMarket(o.ticker, o.side);
                    return (
                      <tr key={o.orderId || `${o.ticker}-${o.side}-${i}`} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><OwnerTag owner={o.owner} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={o.side} betTeam={betTeam} /></td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(o.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {o.priceCents != null ? `${o.priceCents}¢` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Org trades/fills — owner-tagged, including unattributed rows. */}
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-4 py-2.5">
              <h2 className="text-foreground text-sm font-medium">
                Trades <span className="text-muted-foreground">· {visibleFills.length}</span>
              </h2>
            </div>
            {visibleFills.length === 0 && fillsLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : visibleFills.length === 0 ? (
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">No trades</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                    <th className="px-4 py-2 text-left">Market</th>
                    <th className="px-3 py-2 text-left">Venue</th>
                    <th className="px-3 py-2 text-left">Owner</th>
                    <th className="px-3 py-2 text-center">Side</th>
                    <th className="px-3 py-2 text-center">Action</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Fee</th>
                    <th className="px-4 py-2 text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFills.slice(0, 80).map((f, i) => {
                    const polyMarket = f.venue === 'polymarket'
                      ? describePolyMarketCached(f.ticker)
                      : null;
                    const kalshiMarket = f.venue === 'kalshi'
                      ? describeMarket(f.ticker, f.side)
                      : null;
                    const marketLabel = polyMarket?.matchup ?? kalshiMarket?.marketLabel ?? f.ticker;
                    const betTeam = kalshiMarket?.betTeam ?? null;
                    const when = f.executedAt ? new Date(f.executedAt) : null;
                    const timeLabel = when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : '—';
                    return (
                      <tr key={f.fillId || `${f.ticker}-${f.side}-${i}`} className="border-border/40 border-b">
                        <td className="text-foreground px-4 py-2 text-xs">{marketLabel}</td>
                        <td className="px-3 py-2"><VenueTag venue={f.venue} /></td>
                        <td className="px-3 py-2"><OwnerTag owner={f.owner} /></td>
                        <td className="px-3 py-2 text-center"><SideBadge side={f.side} betTeam={betTeam} /></td>
                        <td className="px-3 py-2 text-center">
                          <span className={f.action === 'buy' ? 'text-emerald-400' : 'text-amber-400'}>
                            {f.action.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {Number(f.qty.toFixed(2)).toLocaleString('en-US')}
                        </td>
                        <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                          {f.priceCents != null ? `${f.priceCents}¢` : '—'}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                          {f.feeCents != null ? fmtUsd(f.feeCents / 100) : '—'}
                        </td>
                        <td className="text-muted-foreground px-4 py-2 text-right text-[11px]">{timeLabel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right sidebar — compact per-trader rollup (the "who"), built to fit
            the narrow rail (the full OrgTradersTable's 6 columns overflowed it). */}
        <aside className="sticky top-6 hidden w-72 shrink-0 self-start lg:block">
          <div className="bg-card border-border rounded-lg border">
            <div className="border-border border-b px-3 py-2.5 text-sm font-medium">
              Members <span className="text-muted-foreground">· {members.length}</span>
            </div>
            <div className="max-h-[calc(100vh-9rem)] overflow-y-auto">
              {members.length === 0 ? (
                <p className="text-muted-foreground px-3 py-6 text-center text-sm">No members</p>
              ) : (
                members.map((m) => {
                  const unrealized = computeOrgTraderUnrealized(
                    m,
                    getMarketPrice,
                    undefined,
                    (ticker) => polyFeeSchedules.get(ticker),
                  );
                  const memberPnl =
                    m.realizedPnlNetCents == null || unrealized.valueCents == null
                      ? null
                      : m.realizedPnlNetCents + unrealized.valueCents;
                  return (
                    <button
                      key={m.userId}
                      onClick={() => onTraderClick(m)}
                      className="border-border/40 hover:bg-muted/40 w-full border-b px-3 py-2 text-left transition-colors last:border-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground truncate text-xs font-medium">{m.username}</span>
                        <span
                          className={`shrink-0 font-mono text-xs ${
                            masked || memberPnl == null
                              ? 'text-muted-foreground'
                              : memberPnl >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }`}
                          title={
                            unrealized.complete
                              ? 'Net realized after recorded fees, plus marked unrealized P&L'
                              : `Combined P&L withheld: ${unrealized.pricedPositions}/${unrealized.totalPositions} open positions have both a live mark and close-fee schedule`
                          }
                        >
                          {masked ? '•••' : memberPnl == null ? '—' : fmtUsdSigned(memberPnl / 100)}
                        </span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 text-[11px]">
                        {m.openCount} ctr · {m.openOrderCount ?? 0} ord · {fmtUsd(m.openNotionalCents / 100)}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
